import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import type { ApiResponse, PaginatedResponse } from '@demoops/shared-types';
import type {
  InboxFilters,
  InboxResponse,
  PurchaseRequestComment,
  PurchaseRequestCreateData,
  PurchaseRequestDetail,
  PurchaseRequestFilters,
  PurchaseRequestListRow,
  PurchaseRequestUpdateData,
} from '../types';

/**
 * Purchase Request hooks — slice S1 (PO-FE-01), frozen contract
 * docs/po/PO_API_CONTRACT.md §2. Queries are `retry: false` (invoice idiom).
 * Reads are gated on the caller holding ANY request-visibility permission
 * (view-own / view-project / manage — matrix §1); the server row-scopes, the
 * client merely avoids guaranteed-403 calls. Writes carry `version` and
 * surface 409 STALE_VERSION / INVALID_TRANSITION to the caller (contract §1).
 */

const BASE = '/api/v1/purchase-requests';

export const purchaseRequestKeys = {
  all: ['purchase-requests'] as const,
  lists: () => ['purchase-requests', 'list'] as const,
  list: (filters: PurchaseRequestFilters) => ['purchase-requests', 'list', filters] as const,
  inboxes: () => ['purchase-requests', 'inbox'] as const,
  inbox: (filters: InboxFilters) => ['purchase-requests', 'inbox', filters] as const,
  details: () => ['purchase-requests', 'detail'] as const,
  detail: (id: string) => ['purchase-requests', 'detail', id] as const,
  attachments: (id: string) => ['purchase-requests', 'detail', id, 'attachments'] as const,
  comments: (id: string) => ['purchase-requests', 'detail', id, 'comments'] as const,
};

/** True when the current user may see any purchase-request data. */
export function useCanViewPurchaseRequests(): boolean {
  const { can } = useAuth();
  return (
    can('purchase-requests.view-own') ||
    can('purchase-requests.view-project') ||
    // Every request, read-only (access matrix 2026-10-15): Office, PMs, Estimators.
    can('purchase-requests.view_all') ||
    can('purchase-requests.manage')
  );
}

/** True when the current user may create/submit their own requests. */
export function useCanCreatePurchaseRequests(): boolean {
  return useAuth().can('purchase-requests.create');
}

/* ───────────────────────────── List ───────────────────────────── */

function filtersToQuery(filters: PurchaseRequestFilters): string {
  const q = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v === undefined || v === '' || v === null) return;
    if (Array.isArray(v)) {
      v.forEach((item) => q.append(k, String(item)));
    } else {
      q.append(k, String(v));
    }
  });
  return q.toString();
}

/**
 * Request list (contract §2). The server widens scope by permission
 * (own → project → all); the client never filters rows itself.
 */
export function usePurchaseRequests(filters: PurchaseRequestFilters = {}, enabled = true) {
  const canView = useCanViewPurchaseRequests();
  return useQuery({
    queryKey: purchaseRequestKeys.list(filters),
    queryFn: async () =>
      api.get<PaginatedResponse<PurchaseRequestListRow>>(`${BASE}?${filtersToQuery(filters)}`),
    enabled: canView && enabled,
    retry: false,
  });
}

/* ───────────────────────────── Purchasing Inbox (contract §2.3, S2) ───────────────────────────── */

/**
 * Purchasing Inbox — perm `purchase-requests.manage` ONLY (H5 zero-exposure:
 * the query never fires below the grant). Counts in the response ignore the
 * active bucket; default triage sort is server-side and fixed
 * (equipment_down → urgent → oldest submission).
 */
export function usePurchasingInbox(filters: InboxFilters = {}, enabled = true) {
  const { can } = useAuth();
  return useQuery({
    queryKey: purchaseRequestKeys.inbox(filters),
    queryFn: async () =>
      api.get<InboxResponse>(`${BASE}/inbox?${filtersToQuery(filters)}`),
    enabled: can('purchase-requests.manage') && enabled,
    retry: false,
  });
}

/**
 * Take ownership — transition R2 (`purchase-requests.manage`). Taking an
 * already-owned request REASSIGNS it (H4; previous owner lands in history
 * metadata server-side). Carries `version`; stale → 409 STALE_VERSION.
 */
export function useTakeOwnership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, ...body }: { requestId: string; version: number }) =>
      (await api.post<ApiResponse<PurchaseRequestDetail>>(`${BASE}/${requestId}/take-ownership`, body)).data,
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: purchaseRequestKeys.inboxes() });
      invalidateRequestWrites(qc, vars.requestId);
    },
  });
}

/* ───────────────────────────── Detail ───────────────────────────── */

/**
 * One request's full resource incl. lines, history, and the server `can` map
 * that drives every action button (the UI never guesses permissions).
 */
export function usePurchaseRequest(id: string | undefined) {
  const canView = useCanViewPurchaseRequests();
  return useQuery({
    queryKey: purchaseRequestKeys.detail(id ?? ''),
    queryFn: async () =>
      (await api.get<ApiResponse<PurchaseRequestDetail>>(`${BASE}/${id}`)).data,
    enabled: canView && !!id,
    retry: false,
  });
}

/* ───────────────────────────── Writes ───────────────────────────── */

/** Refresh every surface a request write can move: list + this detail. */
function invalidateRequestWrites(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: purchaseRequestKeys.lists() });
  if (id) qc.invalidateQueries({ queryKey: purchaseRequestKeys.detail(id) });
}

/**
 * Create a draft (perm `purchase-requests.create`). `offline_id` (uuid) is
 * REQUIRED — the server treats a replay with the same offline_id as the same
 * request and returns the original, never a duplicate (contract §1).
 */
export function useCreatePurchaseRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: PurchaseRequestCreateData) =>
      (await api.post<ApiResponse<PurchaseRequestDetail>>(BASE, data)).data,
    onSuccess: (req) => invalidateRequestWrites(qc, req?.id),
  });
}

/**
 * Update a request (requester while draft/info_needed; manage after). Lines
 * replace WHOLESALE (data-model rule). Carries `version`; a stale write
 * returns 409 STALE_VERSION → caller shows the reload banner.
 */
export function useUpdatePurchaseRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: PurchaseRequestUpdateData) =>
      (await api.put<ApiResponse<PurchaseRequestDetail>>(`${BASE}/${id}`, data)).data,
    onSuccess: () => invalidateRequestWrites(qc, id),
  });
}

/**
 * Submit to Purchasing — transition R1, ONLINE-ONLY (workflow §1: the server
 * validates completeness; the client must never optimistic-fake a submit).
 */
export function useSubmitPurchaseRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { version: number }) =>
      (await api.post<ApiResponse<PurchaseRequestDetail>>(`${BASE}/${id}/submit`, data)).data,
    onSuccess: () => invalidateRequestWrites(qc, id),
  });
}

/**
 * Submit a request whose id is only known AFTER the draft create resolves
 * (the wizard). Same endpoint as `useSubmitPurchaseRequest`, id in variables
 * (useSimpleAssignForInvoice idiom).
 */
export function useSubmitPurchaseRequestFor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, ...body }: { requestId: string; version: number }) =>
      (await api.post<ApiResponse<PurchaseRequestDetail>>(`${BASE}/${requestId}/submit`, body)).data,
    onSuccess: (_d, vars) => invalidateRequestWrites(qc, vars.requestId),
  });
}

/**
 * Answer "More Information Needed" and return the SAME request to Purchasing —
 * transition R4 (never a new request; the purchasing owner is retained).
 */
export function useResubmitPurchaseRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { version: number; answer?: string }) =>
      (await api.post<ApiResponse<PurchaseRequestDetail>>(`${BASE}/${id}/resubmit`, data)).data,
    onSuccess: () => invalidateRequestWrites(qc, id),
  });
}

/**
 * Ask for more information — transition R3 (`purchase-requests.manage`,
 * question REQUIRED). Returns the request to the requester; the question is
 * stored as a comment + history metadata and the requester is notified.
 */
export function useRequestInfo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { version: number; question: string }) =>
      (await api.post<ApiResponse<PurchaseRequestDetail>>(`${BASE}/${id}/request-info`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: purchaseRequestKeys.inboxes() });
      invalidateRequestWrites(qc, id);
    },
  });
}

/**
 * Close without PO — transition R9 (`purchase-requests.manage`, reason
 * REQUIRED). Remaining open lines close; converted lines stay untouched.
 */
export function useCloseWithoutPo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { version: number; reason: string }) =>
      (await api.post<ApiResponse<PurchaseRequestDetail>>(`${BASE}/${id}/close-without-po`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: purchaseRequestKeys.inboxes() });
      invalidateRequestWrites(qc, id);
    },
  });
}

/* ─────────────────── Comments (ACC-P2 F2) ───────────────────
 * The PR comment endpoints have lived on master since S1 (index/store ONLY —
 * there is NO resolve route, so the conversation mounts without a resolve
 * adapter member). Client gate mirrors the PR detail: any request-read
 * permission; the server row-scopes (own / project / manage).
 */

/** POST …/comments body — StorePurchaseRequestCommentRequest. */
export interface PurchaseRequestCommentInput {
  comment: string;
  is_question?: boolean;
  parent_comment_id?: string;
  mention_user_ids?: string[];
  /** AD identities to @mention — provisioned just-in-time server-side. */
  directory_mentions?: { ad_username: string; email?: string | null; display_name?: string | null }[];
}

export function usePurchaseRequestComments(id: string | undefined, enabled = true) {
  const canView = useCanViewPurchaseRequests();
  return useQuery({
    queryKey: purchaseRequestKeys.comments(id ?? ''),
    queryFn: async () =>
      (await api.get<PaginatedResponse<PurchaseRequestComment>>(`${BASE}/${id}/comments?per_page=100`)).data,
    enabled: canView && !!id && enabled,
    retry: false,
  });
}

export function useAddPurchaseRequestComment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PurchaseRequestCommentInput) =>
      (await api.post<ApiResponse<PurchaseRequestComment>>(`${BASE}/${id}/comments`, input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: purchaseRequestKeys.comments(id) }),
  });
}

/**
 * Cancel — R7 (own draft, no reason) / R8 (post-draft, reason REQUIRED).
 * Blocked server-side once any line is converted.
 */
export function useCancelPurchaseRequest(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { version: number; reason?: string }) =>
      (await api.post<ApiResponse<PurchaseRequestDetail>>(`${BASE}/${id}/cancel`, data)).data,
    onSuccess: () => invalidateRequestWrites(qc, id),
  });
}
